import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Helmet } from 'react-helmet-async';
import {
  CheckCircleOutline,
  LockResetOutlined,
  SecurityOutlined,
  TimerOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material';

import API from '../data/api';
import { extractFormValues, initialiseForm, isNullValue, validateForm } from 'src/utils/formUtil';
import CustomPasswordInput from 'src/components/custom/CustomPasswordInput';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import ForgotPassword from 'src/sections/auth/login/ForgotPassword';

// Initial form data
const passwordError =
  "Password must be at least 8 characters long, have 1 number, 1 uppercase letter, and 1 special character";

const initialFormData = {
  // token: { value: '', required: false, error: false, errorMessage: 'Please select transport company' },
  password: { value: '', required: true, error: false, errorMessage: passwordError },
  confirmPassword: { value: '', required: true, error: false, errorMessage: 'Confirm password is required and must match' },
};

const passwordRegEx = new RegExp(
  "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$"
);

const requestErrorMessage = 'Invalid token, ensure the URL is correct';

const PASSWORD_RULES = [
  'Minimum 8 characters',
  'At least 1 uppercase letter',
  'At least 1 number',
  'At least 1 special character',
];

const SECURITY_NOTES = [
  {
    title: 'Secure reset link',
    description: 'Your password can only be changed with the reset token from your email.',
    icon: VpnKeyOutlined,
  },
  {
    title: 'Token expiry',
    description: 'If the link has expired, request a fresh reset email and use the latest message.',
    icon: TimerOutlined,
  },
  {
    title: 'Account protection',
    description: 'Choose a password that is not reused on other business systems.',
    icon: SecurityOutlined,
  },
];

function ResetPassword() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const initFormState = initialiseForm(initialFormData);
  const [formData, setFormData] = useState({ ...initFormState });

  const [requestError, setRequestError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [openForgot, setOpenForgot] = useState(false);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitted(true);

    if (!validateForm(setFormData, formData)) {
      console.error("Form submission failed",)
      setSubmitted(false);
      return;
    }

    if (formData.password.value !== formData.confirmPassword.value) {
      setConfirmPasswordError(true);
      setSubmitted(false);
      return;
    }

    if (isNullValue(token)) {
      handleRequestError(requestErrorMessage);
      setSubmitted(false);
      return;
    }

    const values = extractFormValues(formData);
    try {
      // Send the POST request with JSON body
      const response = await API.post('auth/reset-password', {
        token: token,
        password: values?.password
      });
      if (response.status === 200) {
        // Reset the form if response is successful
        showSnackbar({
          message: `Password reset was successful, proceed to login`,
          type: "success",
        });
        navigate("/login");
      } else {
        handleRequestError(requestErrorMessage);
      }
    } catch (error) {
      console.log(error);
      if (error.status === 403) {
        handleRequestError("Token has expired, request a new one");
        setOpenForgot(true);
      } else {
        handleRequestError(requestErrorMessage);
      }
    }
    setSubmitted(false);
  };

  const handleRequestError = (message) => {
    setRequestError(true);
    showSnackbar({
      message: message,
      type: "error",
    });
  }

  const checkPasswordMatch = (e) => {
    const nextPassword = e.target.name === 'password' ? e.target.value : formData.password.value;
    const nextConfirmPassword = e.target.name === 'confirmPassword' ? e.target.value : formData.confirmPassword.value;
    let confError = formData.confirmPassword.error;

    if (e.target.name === 'password') {
      confError = Boolean(nextConfirmPassword) && nextPassword !== nextConfirmPassword;
    }
    else if (e.target.name === 'confirmPassword') {
      confError = !passwordRegEx.test(e.target.value) || nextPassword !== nextConfirmPassword;
    }

    setConfirmPasswordError(confError);
  }

  const handleFormDataChange = (e) => {
    checkPasswordMatch(e);

    setFormData({
      ...formData,
      [e.target.name]: {
        ...formData[e.target.name],
        value: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        error: !(e.target.type === "password" ? passwordRegEx.test(e.target.value) : e.target.validity.valid),
      },
    });

  };


  const handleClickForgetPassword = () => {
    setOpenForgot(!openForgot);
  };


  return (
    <>
      <Helmet>
        <title>Reset Password</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
          backgroundImage:
            "linear-gradient(105deg, rgba(3, 16, 31, 0.86) 0%, rgba(6, 31, 53, 0.72) 48%, rgba(8, 24, 44, 0.88) 100%), url('/assets/background-2.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          px: 2,
          py: { xs: 3, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  background:
                    'linear-gradient(160deg, rgba(11,27,46,0.88) 0%, rgba(8,21,35,0.84) 100%)',
                  backdropFilter: 'blur(8px)',
                  color: '#E5F4FF',
                }}
              >
                <Box
                  component="img"
                  alt="HoneyBadger"
                  src="/assets/images/hb-wordmark-light.png"
                  sx={{ height: 44, mb: 2 }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 1.5,
                    fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                  }}
                >
                  Reset access securely
                </Typography>
                <Typography sx={{ color: 'rgba(229,244,255,0.86)', mb: 3, maxWidth: 620 }}>
                  Create a new password for your HoneyBadger ERP + TMS account and return to
                  operations, logistics, finance, and document workflows.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
                  {PASSWORD_RULES.map((rule) => (
                    <Chip
                      key={rule}
                      icon={<CheckCircleOutline />}
                      label={rule}
                      size="small"
                      sx={{
                        color: '#D4EBFF',
                        backgroundColor: 'rgba(73, 148, 206, 0.2)',
                        border: '1px solid rgba(122, 180, 224, 0.34)',
                        '& .MuiChip-icon': { color: '#34d399' },
                      }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ borderColor: 'rgba(170,210,240,0.2)', mb: 2.5 }} />

                <Grid container spacing={2}>
                  {SECURITY_NOTES.map((note) => {
                    const Icon = note.icon;
                    return (
                      <Grid size={{ xs: 12 }} key={note.title}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderLeft: '4px solid #38bdf8',
                            backgroundColor: 'rgba(255,255,255,0.06)',
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                            <Icon sx={{ color: '#38bdf8' }} />
                            <Typography sx={{ fontWeight: 600 }}>{note.title}</Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'rgba(229,244,255,0.82)' }}>
                            {note.description}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 1, md: 2 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.94)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack spacing={2.5}>
                  <Box
                    component="img"
                    alt="Honey Badger"
                    src="/assets/images/hb-logo-only-dark.png"
                    sx={{ width: 82, height: 82, alignSelf: 'center' }}
                  />
                  <Box>
                    <Typography
                      component="h1"
                      variant="h4"
                      sx={{
                        textAlign: 'center',
                        fontWeight: 700,
                        fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                      }}
                    >
                      Set a new password
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                      Enter and confirm your new password to complete account recovery.
                    </Typography>
                  </Box>

                  {isNullValue(token) && (
                    <Alert severity="warning">
                      This reset link is missing a token. Request a new password reset email.
                    </Alert>
                  )}

                  {requestError && (
                    <Alert severity="error">
                      We could not complete this reset request. Request a new token if the link has
                      expired.
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3}>
                      <CustomPasswordInput
                        name="password"
                        label="New password"
                        value={formData.password.value}
                        onChange={handleFormDataChange}
                        required={formData.password.required}
                        error={formData.password.error}
                        helperText={formData.password.error && formData.password.errorMessage}
                      />

                      <CustomPasswordInput
                        name="confirmPassword"
                        label="Confirm new password"
                        value={formData.confirmPassword.value}
                        onChange={handleFormDataChange}
                        required={formData.confirmPassword.required}
                        error={confirmPasswordError}
                        helperText={confirmPasswordError && formData.confirmPassword.errorMessage}
                      />
                    </Stack>

                    <LoadingButton
                      sx={{ mt: 3, mb: 1.5, backgroundColor: '#3B3297' }}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={submitted}
                      startIcon={<LockResetOutlined />}
                    >
                      Reset Password
                    </LoadingButton>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Typography sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                      Remembered it?{' '}
                      <Link
                        sx={{ cursor: 'pointer' }}
                        variant="body2"
                        onClick={() => {
                          navigate('/login');
                        }}
                      >
                        Log in
                      </Link>
                    </Typography>
                    <Typography sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Link
                        sx={{ cursor: 'pointer' }}
                        variant="body2"
                        onClick={handleClickForgetPassword}
                      >
                        Request new token
                      </Link>
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ForgotPassword
        open={openForgot}
        handleClose={handleClickForgetPassword}
        title={"Request New Token"}
      />
    </>
  );
}

ResetPassword.propTypes = {

};

export default ResetPassword;
